import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Button, Chip, CircularProgress, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid,
  Alert, Snackbar,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
} from '@mui/icons-material';
import { registrationApi } from '../../api/registrationApi';
import { masterApi } from '../../api/masterApi';
import { RegistrationRequest, RegistrationRequestDetail, DropdownItem } from '../../types';

const statusColor = (status: string): 'warning' | 'success' | 'error' | 'default' => {
  switch (status) {
    case 'Pending': return 'warning';
    case 'Approved': return 'success';
    case 'Rejected': return 'error';
    default: return 'default';
  }
};

const RegistrationRequests: React.FC = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<RegistrationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewOpen, setViewOpen] = useState(false);
  const [detail, setDetail] = useState<RegistrationRequestDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Dropdown data for display
  const [genders, setGenders] = useState<DropdownItem[]>([]);
  const [departments, setDepartments] = useState<DropdownItem[]>([]);
  const [designations, setDesignations] = useState<DropdownItem[]>([]);
  const [branches, setBranches] = useState<DropdownItem[]>([]);
  const [employmentTypes, setEmploymentTypes] = useState<DropdownItem[]>([]);
  const [cities, setCities] = useState<DropdownItem[]>([]);
  const [states, setStates] = useState<DropdownItem[]>([]);
  const [countries, setCountries] = useState<DropdownItem[]>([]);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await registrationApi.getAll();
      setRequests(data);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load requests', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests();
    const loadDropdowns = async () => {
      try {
        const [g, d, des, b, et, c, s, co] = await Promise.all([
          masterApi.getGenders(), masterApi.getDepartments(), masterApi.getDesignations(),
          masterApi.getBranches(), masterApi.getEmploymentType(), masterApi.getCities(),
          masterApi.getStates(), masterApi.getCountries(),
        ]);
        setGenders(g); setDepartments(d); setDesignations(des); setBranches(b);
        setEmploymentTypes(et); setCities(c); setStates(s); setCountries(co);
      } catch { /* silent */ }
    };
    loadDropdowns();
  }, [loadRequests]);

  const handleView = async (id: string) => {
    setDetailLoading(true);
    setViewOpen(true);
    try {
      const data = await registrationApi.getById(id);
      setDetail(data);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load details', severity: 'error' });
      setViewOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    const result = await Swal.fire({
      title: 'Approve Registration',
      text: 'Are you sure you want to approve this registration?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#667eea',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, approve',
    });
    if (!result.isConfirmed) return;
    setProcessing(true);
    try {
      await registrationApi.approve(id);
      setSnackbar({ open: true, message: 'Registration approved! Employee created.', severity: 'success' });
      setViewOpen(false);
      loadRequests();
    } catch {
      setSnackbar({ open: true, message: 'Failed to approve registration', severity: 'error' });
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectOpen = (id: string) => {
    setRejectId(id);
    setRejectReason('');
    setRejectOpen(true);
  };

  const handleReject = async () => {
    if (!rejectId || !rejectReason.trim()) return;
    setProcessing(true);
    try {
      await registrationApi.reject(rejectId, rejectReason);
      setSnackbar({ open: true, message: 'Registration rejected.', severity: 'success' });
      setRejectOpen(false);
      setViewOpen(false);
      loadRequests();
    } catch {
      setSnackbar({ open: true, message: 'Failed to reject registration', severity: 'error' });
    } finally {
      setProcessing(false);
    }
  };

  const getDropdownName = (id: string | null | undefined, items: DropdownItem[]): string => {
    if (!id) return '-';
    const item = items.find(i => String(i.id) === String(id));
    return item?.name || id;
  };

  const renderField = (label: string, value: string | null | undefined) => (
    <Grid size={{ xs: 12, sm: 3 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{value || '-'}</Typography>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/employees')}><BackIcon /></IconButton>
        <Typography variant="h5" fontWeight="bold">Employee Registration Requests</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Total Records: {requests.length}
      </Typography>
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Employee Name</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Mobile</strong></TableCell>
                <TableCell><strong>Applied Date</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No registration requests found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((req) => (
                  <TableRow key={req.id} hover>
                    <TableCell>{req.firstName} {req.lastName}</TableCell>
                    <TableCell>{req.email}</TableCell>
                    <TableCell>{req.mobileNumber || '-'}</TableCell>
                    <TableCell>{new Date(req.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip label={req.status} color={statusColor(req.status)} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => handleView(req.id)}><ViewIcon /></IconButton>
                      </Tooltip>
                      {req.status === 'Pending' && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton size="small" color="success" onClick={() => handleApprove(req.id)} disabled={processing}>
                              <ApproveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton size="small" color="error" onClick={() => handleRejectOpen(req.id)} disabled={processing}>
                              <RejectIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* View Detail Dialog */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Registration Request Details</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : detail ? (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Basic Details</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {renderField('First Name', detail.firstName)}
                {renderField('Last Name', detail.lastName)}
                {renderField('Gender', getDropdownName(detail.gender, genders))}
                {renderField('Date of Birth', detail.dateOfBirth ? new Date(detail.dateOfBirth).toLocaleDateString() : null)}
                {renderField('Email', detail.email)}
                {renderField('Mobile', detail.mobileNumber)}
                {renderField('Aadhaar Number', detail.aadhaarNumber)}
                {renderField('PAN Number', detail.panNumber)}
              </Grid>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Job Details</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {renderField('Department', detail.departmentName)}
                {renderField('Designation', detail.designationName)}
                {renderField('Branch', detail.branchName)}
                {renderField('Employment Type', detail.employmentType)}
              </Grid>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Address Details</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {renderField('Address', detail.addressLine1)}
                {renderField('City', detail.city)}
                {renderField('State', detail.state)}
                {renderField('Country', detail.country)}
              </Grid>

              {detail.status === 'Rejected' && detail.rejectionReason && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <strong>Rejection Reason:</strong> {detail.rejectionReason}
                </Alert>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
          {detail?.status === 'Pending' && (
            <>
              <Button variant="contained" color="error" onClick={() => { setViewOpen(false); handleRejectOpen(detail.id); }}
                disabled={processing}>Reject</Button>
              <Button variant="contained" color="success" onClick={() => handleApprove(detail.id)}
                disabled={processing}>Approve</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Reject Reason Dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Registration</DialogTitle>
        <DialogContent>
          <TextField label="Rejection Reason" fullWidth multiline rows={3} value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}
            disabled={processing || !rejectReason.trim()}>
            {processing ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RegistrationRequests;
